import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '../store'
import { 
  IFaturamentoConfiguracao, 
  INotaFiscalModelo, 
  IFaturamentoConfiguracaoCompleta,
  IFaturamentoConfiguracaoSistema,
  IDescricaoPadraoNotaFiscal,
  ISubtitulosDisponiveis,
  IDataPadraoVencimento
} from '../../models'

interface FaturamentoState {
  configuracao: IFaturamentoConfiguracao | null
  modelos: INotaFiscalModelo[]
  faturamentoCompleto: IFaturamentoConfiguracaoCompleta[]
  // NOVOS ESTADOS PARA CONFIGURAÇÕES DO SISTEMA
  configuracoesSistema: IFaturamentoConfiguracaoSistema | null
  descricaoPadraoNotaFiscal: IDescricaoPadraoNotaFiscal | null
  subtitulosDisponiveis: ISubtitulosDisponiveis | null
  dataPadraoVencimento: IDataPadraoVencimento | null
  loading: boolean
  error: string | null
}

const initialState: FaturamentoState = {
  configuracao: null,
  modelos: [],
  faturamentoCompleto: [],
  // Estados iniciais para configurações
  configuracoesSistema: null,
  descricaoPadraoNotaFiscal: null,
  subtitulosDisponiveis: null,
  dataPadraoVencimento: null,
  loading: false,
  error: null
}

const faturamentoSlice = createSlice({
  name: 'faturamento',
  initialState,
  reducers: {
    setConfiguracao: (state, action: PayloadAction<IFaturamentoConfiguracao | null>) => {
      state.configuracao = action.payload
    },
    setModelos: (state, action: PayloadAction<INotaFiscalModelo[]>) => {
      state.modelos = action.payload
    },
    setFaturamentoCompleto: (state, action: PayloadAction<IFaturamentoConfiguracaoCompleta[]>) => {
      state.faturamentoCompleto = action.payload
    },
    // NOVOS REDUCERS PARA CONFIGURAÇÕES
    setConfiguracoesSistema: (state, action: PayloadAction<IFaturamentoConfiguracaoSistema | null>) => {
      state.configuracoesSistema = action.payload
    },
    setDescricaoPadraoNotaFiscal: (state, action: PayloadAction<IDescricaoPadraoNotaFiscal | null>) => {
      state.descricaoPadraoNotaFiscal = action.payload
    },
    setSubtitulosDisponiveis: (state, action: PayloadAction<ISubtitulosDisponiveis | null>) => {
      state.subtitulosDisponiveis = action.payload
    },
    setDataPadraoVencimento: (state, action: PayloadAction<IDataPadraoVencimento | null>) => {
      state.dataPadraoVencimento = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    resetFaturamento: (state) => {
      state.configuracao = null
      state.modelos = []
      state.faturamentoCompleto = []
      state.configuracoesSistema = null
      state.descricaoPadraoNotaFiscal = null
      state.subtitulosDisponiveis = null
      state.dataPadraoVencimento = null
      state.loading = false
      state.error = null
    }
  }
})

export const {
  setConfiguracao,
  setModelos,
  setFaturamentoCompleto,
  setConfiguracoesSistema,
  setDescricaoPadraoNotaFiscal,
  setSubtitulosDisponiveis,
  setDataPadraoVencimento,
  setLoading,
  setError,
  resetFaturamento
} = faturamentoSlice.actions

export const faturamentoReducer = faturamentoSlice.reducer

// Hook personalizado para usar o Redux do faturamento
export const useFaturamentoRedux = () => {
  const dispatch = useDispatch<AppDispatch>()
  
  // Selectors
  const configuracao = useSelector((state: RootState) => state.faturamento.configuracao)
  const modelos = useSelector((state: RootState) => state.faturamento.modelos)
  const faturamentoCompleto = useSelector((state: RootState) => state.faturamento.faturamentoCompleto)
  const configuracoesSistema = useSelector((state: RootState) => state.faturamento.configuracoesSistema)
  const descricaoPadraoNotaFiscal = useSelector((state: RootState) => state.faturamento.descricaoPadraoNotaFiscal)
  const subtitulosDisponiveis = useSelector((state: RootState) => state.faturamento.subtitulosDisponiveis)
  const dataPadraoVencimento = useSelector((state: RootState) => state.faturamento.dataPadraoVencimento)
  const loading = useSelector((state: RootState) => state.faturamento.loading)
  const error = useSelector((state: RootState) => state.faturamento.error)

  // Actions
  const setConfiguracaoAction = (configuracao: IFaturamentoConfiguracao | null) => dispatch(setConfiguracao(configuracao))
  const setModelosAction = (modelos: INotaFiscalModelo[]) => dispatch(setModelos(modelos))
  const setFaturamentoCompletoAction = (faturamento: IFaturamentoConfiguracaoCompleta[]) => dispatch(setFaturamentoCompleto(faturamento))
  const setConfiguracoesSistemaAction = (configs: IFaturamentoConfiguracaoSistema | null) => dispatch(setConfiguracoesSistema(configs))
  const setDescricaoPadraoNotaFiscalAction = (descricao: IDescricaoPadraoNotaFiscal | null) => dispatch(setDescricaoPadraoNotaFiscal(descricao))
  const setSubtitulosDisponiveisAction = (subtitulos: ISubtitulosDisponiveis | null) => dispatch(setSubtitulosDisponiveis(subtitulos))
  const setDataPadraoVencimentoAction = (data: IDataPadraoVencimento | null) => dispatch(setDataPadraoVencimento(data))
  const setLoadingAction = (loading: boolean) => dispatch(setLoading(loading))
  const setErrorAction = (error: string | null) => dispatch(setError(error))
  const resetFaturamentoAction = () => dispatch(resetFaturamento())

  return {
    // State
    configuracao,
    modelos,
    faturamentoCompleto,
    configuracoesSistema,
    descricaoPadraoNotaFiscal,
    subtitulosDisponiveis,
    dataPadraoVencimento,
    loading,
    error,
    
    // Actions
    setConfiguracao: setConfiguracaoAction,
    setModelos: setModelosAction,
    setFaturamentoCompleto: setFaturamentoCompletoAction,
    setConfiguracoesSistema: setConfiguracoesSistemaAction,
    setDescricaoPadraoNotaFiscal: setDescricaoPadraoNotaFiscalAction,
    setSubtitulosDisponiveis: setSubtitulosDisponiveisAction,
    setDataPadraoVencimento: setDataPadraoVencimentoAction,
    setLoading: setLoadingAction,
    setError: setErrorAction,
    resetFaturamento: resetFaturamentoAction
  }
}
